import React, { useRef } from "react";
import { MAX_GALLERY_IMAGES } from "../../utils/image/image-constants";
import { ALLOWED_SVG_EXTENSIONS } from "../../utils/image/image-constants";
import { readFileAsText, validateSvgFile } from "../../utils/image/image-file-utils";

function GallerySvgItem({ svgContent, onRemove, disabled, removeLabel }) {
    return (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 aspect-square p-2">
            <div
                className="h-full w-full flex items-center justify-center [&>svg]:max-h-full [&>svg]:max-w-full"
                dangerouslySetInnerHTML={{ __html: svgContent }}
            />
            <button
                type="button"
                onClick={onRemove}
                disabled={disabled}
                className="absolute top-2 right-2 rounded-lg bg-white/95 px-2 py-1 text-xs font-semibold text-red-600 shadow border border-red-100 hover:bg-red-50 disabled:opacity-50"
            >
                {removeLabel}
            </button>
        </div>
    );
}

export default function GallerySvgUpload({
    images = [],
    onChange,
    t,
    disabled = false,
    onError,
}) {
    const inputRef = useRef(null);
    const canAddMore = images.length < MAX_GALLERY_IMAGES;

    const handleSelectFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !canAddMore) return;

        const validation = validateSvgFile(file);
        if (!validation.valid) {
            onError?.(t(validation.key));
            return;
        }

        try {
            const svgContent = await readFileAsText(file);
            if (!svgContent.trim().startsWith("<svg")) {
                onError?.(t("alert_svg_formato_invalido"));
                return;
            }
            onChange([...images, svgContent.trim()]);
        } catch (err) {
            onError?.(err.message ?? t("alert_process_failed"));
        }
    };

    const handleRemove = (index) => {
        onChange(images.filter((_, i) => i !== index));
    };

    return (
        <div>
            {canAddMore && (
                <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center cursor-pointer hover:border-ct-primary-400 hover:bg-ct-primary-50/30 transition">
                    <span className="text-2xl" aria-hidden>📄</span>
                    <span className="text-sm font-semibold text-slate-700">{t("label_agregar_svg")}</span>
                    <span className="text-xs text-slate-500">{t("label_galeria_una_por_vez")}</span>
                    <span className="text-xs text-slate-500">{t("label_galeria_max_imagenes", { count: MAX_GALLERY_IMAGES })}</span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ALLOWED_SVG_EXTENSIONS.join(",")}
                        className="sr-only"
                        disabled={disabled}
                        onChange={handleSelectFile}
                    />
                </label>
            )}

            {images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {images.map((svgContent, index) => (
                        <GallerySvgItem
                            key={`svg-${index}`}
                            svgContent={svgContent}
                            disabled={disabled}
                            removeLabel={t("label_eliminar")}
                            onRemove={() => handleRemove(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
